import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, CheckCircle2, Clock, XCircle, Truck, Archive, MapPin, ChevronDown, ChevronUp, ExternalLink, Loader2, RotateCcw, FileText, ClipboardList } from 'lucide-react';
import { fmtDate } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { downloadPickingSlip } from '@/lib/pickingSlips';
import OrderTimeline from './OrderTimeline';
import OrderDownloadsList from './OrderDownloadsList';

const STATUS_CONFIG = {
  pending:            { label: 'PENDING',        icon: Clock,        color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  paid:               { label: 'CONFIRMED',       icon: CheckCircle2, color: 'text-green-400',  bg: 'bg-green-400/10' },
  failed:             { label: 'FAILED',          icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-400/10' },
  in_production:      { label: 'IN PRODUCTION',   icon: Package,      color: 'text-blue-400',   bg: 'bg-blue-400/10' },
  packed:             { label: 'PACKED',          icon: Package,      color: 'text-indigo-400', bg: 'bg-indigo-400/10' },
  dispatched:         { label: 'SHIPPED',         icon: Truck,        color: 'text-primary',    bg: 'bg-primary/10' },
  delivered:          { label: 'DELIVERED',       icon: MapPin,       color: 'text-green-400',  bg: 'bg-green-400/10' },
  completed:          { label: 'COMPLETED',       icon: CheckCircle2, color: 'text-green-400',  bg: 'bg-green-400/10' },
  refunded:           { label: 'REFUNDED',        icon: RotateCcw,    color: 'text-orange-400', bg: 'bg-orange-400/10' },
  partially_refunded: { label: 'PART. REFUNDED',  icon: RotateCcw,    color: 'text-orange-400', bg: 'bg-orange-400/10' },
};

function OrderCard({ order }) {
  const [open, setOpen] = useState(false);
  const [pickingBusy, setPickingBusy] = useState(false);
  const { toast } = useToast();
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const Icon = status.icon;

  const handlePickingSlip = async () => {
    setPickingBusy(true);
    try {
      await downloadPickingSlip(order.id);
    } catch (e) {
      toast({ title: 'Picking slip failed', description: e.message, variant: 'destructive' });
    }
    setPickingBusy(false);
  };

  return (
    <div className="bg-[#111115] border border-white/5">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-start justify-between p-6 text-left">
        <div className="flex-1 pr-4">
          <p className="text-white font-bold text-sm">{order.product_name || 'Product'}</p>
          <p className="text-white/30 text-xs mt-1">
            Order #{order.id?.slice(-8).toUpperCase()} · {fmtDate(order.created_date) || '—'}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`flex items-center gap-1.5 text-xs font-bold tracking-wider px-3 py-1.5 ${status.bg} ${status.color}`}>
            <Icon className="w-3.5 h-3.5" /> {status.label}
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </div>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-white/5 space-y-4">
          {/* Order progress timeline */}
          <OrderTimeline status={order.status} />

          {/* Tracking number */}
          {order.tracking_number && (
            <div className="bg-[#0d0d0f] border border-white/5 p-4 flex items-center justify-between">
              <div>
                <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">TRACKING NUMBER</p>
                <p className="text-white font-mono text-sm">{order.tracking_number}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-white/20" />
            </div>
          )}

          {/* Delivery address */}
          {order.delivery_address_line1 && (
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">DELIVERY ADDRESS</p>
              <p className="text-white/50 text-sm leading-relaxed">
                {[order.delivery_address_line1, order.delivery_address_line2, order.delivery_city, order.delivery_postcode].filter(Boolean).join(', ')}
              </p>
            </div>
          )}

          {/* Downloads */}
          <OrderDownloadsList orderId={order.id} />

          {/* Invoice download */}
          {order.invoice_url && (
            <a
              href={order.invoice_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/[0.03] border border-white/10 hover:border-primary/40 px-4 py-2.5 text-white/60 hover:text-white transition-all w-fit"
            >
              <FileText className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-bold tracking-wider">DOWNLOAD INVOICE</span>
            </a>
          )}

          {/* Picking slip */}
          <button
            onClick={handlePickingSlip}
            disabled={pickingBusy}
            className="flex items-center gap-2 bg-white/[0.03] border border-white/10 hover:border-primary/40 px-4 py-2.5 text-white/60 hover:text-white transition-all w-fit disabled:opacity-50"
          >
            {pickingBusy ? <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" /> : <ClipboardList className="w-3.5 h-3.5 text-primary" />}
            <span className="text-xs font-bold tracking-wider">DOWNLOAD PICKING SLIP</span>
          </button>

          {/* Price row */}
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <span className="text-white/40 text-sm">Order Total</span>
            <span className="text-primary font-black text-2xl">£{order.amount?.toFixed(2)}</span>
          </div>
          {order.refunded_amount > 0 && (
            <div className="flex items-center justify-between -mt-2">
              <span className="text-orange-400/60 text-xs">Refunded</span>
              <span className="text-orange-400 font-bold">−£{order.refunded_amount.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrdersList({ email }) {
  const queryClient = useQueryClient();
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders', email],
    queryFn: () => email
      ? base44.entities.Order.filter({ customer_email: email }, '-created_date')
      : base44.entities.Order.list('-created_date'),
    enabled: true,
  });

  // Live-update the list whenever any Order record changes (e.g. admin edits),
  // since global query refetching is disabled on window focus.
  useEffect(() => {
    const unsubscribe = base44.entities.Order.subscribe(() => {
      queryClient.invalidateQueries({ queryKey: ['orders', email] });
    });
    return unsubscribe;
  }, [email, queryClient]);

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  // Records folded into a master order by the merge tool are hidden so the
  // customer sees the single consolidated sale, not the duplicate records.
  const visible = orders.filter((o) => !o.merged_into_order_id);
  const sorted = [...visible].sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

  if (sorted.length === 0) {
    return (
      <div className="text-center py-20 border border-white/5 bg-[#111115]">
        <Package className="w-10 h-10 text-white/10 mx-auto mb-4" />
        <p className="text-white/30 text-sm">No orders yet</p>
        <p className="text-white/20 text-xs mt-1">Your purchases will appear here after checkout.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-white/30 text-xs font-bold tracking-widest mb-2">{sorted.length} ORDER{sorted.length !== 1 ? 'S' : ''}</p>
      {sorted.map(order => <OrderCard key={order.id} order={order} />)}
    </div>
  );
}